import React from 'react';
import { Link } from 'react-router-dom';
import { ScrollReveal } from '../components/ScrollReveal';

// Image URLs
const IMAGES = {
  founder: "https://customer-assets.emergentagent.com/job_roots-32-learners/artifacts/7kcz17kt_Tai.png",
  agriculture: "https://customer-assets.emergentagent.com/job_roots-32-learners/artifacts/hwyxoe1c_IMG_0366.JPG",
  greenhouse: "https://customer-assets.emergentagent.com/job_roots-32-learners/artifacts/7qgtc9xr_IMG_0177%203.jpg",
};

const timeline = [
  { year: '2026', milestone: 'Runda Pilot Launch', details: '32 learners, 8 Walezi, mixed-age pods. Proving the 2+4 model works.' },
  { year: '2030', milestone: 'Refinement & Documentation', details: 'Codifying curriculum, training methodology, impact measurement.' },
  { year: '2032', milestone: '500 Learners', details: 'Second campus + Walezi training academy launch.' },
  { year: '2037', milestone: '5,000+ Enrolled', details: 'Multiple campuses, open-source model, continent-wide impact.' },
];

export default function Founder() {
  return (
    <div data-testid="founder-page" className="page-transition pt-24">
      {/* Hero Section */}
      <section className="py-28 md:py-36 bg-[#F9F8F4]">
        <div className="container-nova">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            <div>
              <ScrollReveal>
                <p className="text-sm font-medium uppercase tracking-wider text-[#C27856] mb-4">
                  Founder & Head of School
                </p>
              </ScrollReveal>
              
              <ScrollReveal delay={100}>
                <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-[#1A3C34] leading-[1.1]">
                  Taisha Kama Bailey
                </h1>
              </ScrollReveal>
              
              <ScrollReveal delay={200}>
                <p className="mt-10 text-lg font-light leading-relaxed text-[#2D3A3A]">
                  Over twenty years in education across London and Nairobi. Experience spans classroom teaching, curriculum development and school leadership.
                </p>
              </ScrollReveal>
            </div>
            
            <ScrollReveal delay={150}>
              <div className="rounded-2xl overflow-hidden">
                <img 
                  src={IMAGES.founder}
                  alt="Taisha Kama Bailey"
                  className="w-full h-[500px] md:h-[600px] object-cover object-top"
                />
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Why I Built Nova Roots */}
      <section className="py-28 md:py-36 bg-[#F2F0E9]">
        <div className="container-nova">
          <div className="max-w-3xl mx-auto">
            <ScrollReveal>
              <h2 className="font-serif text-2xl md:text-3xl font-normal text-[#1A3C34]">
                Why I Built Nova Roots
              </h2>
            </ScrollReveal>
            
            <ScrollReveal delay={100}>
              <div className="mt-10 space-y-6 text-base font-light leading-relaxed text-[#2D3A3A]">
                <p>
                  Nova Roots was born from my heart as a mother and my conviction as an educator. After more than twenty years in classrooms across London and Nairobi, I saw how traditional systems measured children by test scores instead of their humanity. I watched bright, creative minds fade under the weight of conformity.
                </p>
                
                <p>
                  When I relocated to Kenya, I realised Africa needed a new kind of school—one rooted in dignity, purpose and relevance. Not a copy of Western models, but something that grows from African soil. Something that recognises our children will inherit both unprecedented challenges and extraordinary opportunities.
                </p>
                
                <p>
                  I became obsessed with a question: What if school prepared children for real life? Not just university entrance, but entrepreneurship, leadership, emotional resilience and practical survival skills. What if we blended the best technology with the wisdom of African communal learning? What if children grew their own food, built their own furniture and coded their own solutions?
                </p>
                
                <p className="text-[#5C6B6B]">
                  The 2+4 model emerged from years of research, piloting and listening to children. It's been tested, refined and now—finally—ready to launch with our founding 32 learners in September 2026.
                </p>
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              <div className="mt-12 p-8 bg-[#1A3C34] rounded-2xl">
                <p className="text-base font-light text-[#F9F8F4] leading-relaxed">
                  "This is not a concept born in boardrooms; it is my life's work, shaped by teaching, motherhood and faith in Africa's future. Nova Roots is more than a school. It is a movement to restore dignity to learning and give Africa's children roots to stand tall and wings to soar."
                </p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Leadership During Pilot */}
      <section className="py-28 md:py-36 bg-[#F9F8F4]">
        <div className="container-nova">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            <ScrollReveal>
              <div className="rounded-2xl overflow-hidden">
                <img 
                  src={IMAGES.greenhouse}
                  alt="Learning environment"
                  className="w-full h-[400px] object-cover"
                />
              </div>
            </ScrollReveal>
            
            <div>
              <ScrollReveal>
                <h2 className="font-serif text-2xl md:text-3xl font-normal text-[#1A3C34]">
                  Pilot Phase Leadership
                </h2>
              </ScrollReveal>
              
              <ScrollReveal delay={100}>
                <div className="mt-8 space-y-4 text-sm font-light leading-relaxed text-[#5C6B6B]">
                  <p>
                    Daily on-site presence during the pilot phase. Direct accountability for academic and operational standards.
                  </p>
                  <p>
                    Responsible for model fidelity, family partnership and Walezi development.
                  </p>
                  <p>
                    Growth paced deliberately. Staffing expansion aligned strictly with capacity commitments.
                  </p>
                </div>
              </ScrollReveal>
              
              <ScrollReveal delay={200}>
                <div className="mt-10 p-6 bg-white rounded-xl border border-[#EBE8E0]">
                  <h3 className="font-heading text-sm font-medium uppercase tracking-wider text-[#5C6B6B] mb-4">
                    Background
                  </h3>
                  <ul className="space-y-3 text-sm font-light text-[#2D3A3A]">
                    <li className="flex items-start gap-3">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#C27856] mt-2 flex-shrink-0" />
                      <span>20+ years classroom and leadership experience</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#C27856] mt-2 flex-shrink-0" />
                      <span>Teaching across London and Nairobi</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#C27856] mt-2 flex-shrink-0" />
                      <span>Curriculum development and teacher training</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#C27856] mt-2 flex-shrink-0" />
                      <span>School leadership and operations</span>
                    </li>
                  </ul>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>

      {/* Growth Timeline */}
      <section className="py-28 md:py-36 bg-[#F2F0E9]">
        <div className="container-nova">
          <div className="max-w-3xl mx-auto">
            <ScrollReveal>
              <h2 className="font-serif text-2xl md:text-3xl font-normal text-[#1A3C34] text-center mb-16">
                The Vision: Growth Timeline
              </h2>
            </ScrollReveal>
            
            <ScrollReveal delay={100}>
              <div className="space-y-6">
                {timeline.map((item, index) => (
                  <div key={index} className="flex gap-6 items-start">
                    <div className="w-20 flex-shrink-0">
                      <span className="text-2xl font-heading font-light text-[#C27856]">{item.year}</span>
                    </div>
                    <div className="flex-1 pb-6 border-b border-[#1A3C34]/10 last:border-b-0">
                      <h3 className="font-heading text-base font-medium text-[#1A3C34]">{item.milestone}</h3>
                      <p className="text-sm font-light text-[#5C6B6B] mt-1">{item.details}</p>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Agriculture Image */}
      <section className="py-28 md:py-36 bg-[#F9F8F4]">
        <div className="container-nova">
          <ScrollReveal>
            <div className="rounded-2xl overflow-hidden max-w-3xl mx-auto">
              <img 
                src={IMAGES.agriculture}
                alt="Agricultural learning"
                className="w-full h-[350px] md:h-[400px] object-cover"
              />
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#1A3C34]">
        <div className="container-nova text-center">
          <ScrollReveal>
            <h2 className="font-heading text-2xl md:text-3xl font-light text-[#F9F8F4]">
              Join Us
            </h2>
          </ScrollReveal>
          
          <ScrollReveal delay={150}>
            <p className="mt-6 text-base font-light text-[#F9F8F4]/80 max-w-md mx-auto">
              Be part of the movement to transform education in Africa.
            </p>
          </ScrollReveal>
          
          <ScrollReveal delay={250}>
            <Link 
              to="/enquire" 
              className="mt-10 inline-block bg-[#F9F8F4] text-[#1A3C34] hover:bg-white px-10 py-4 rounded-full text-sm font-medium tracking-wide transition-all duration-300"
              data-testid="founder-enquire-btn"
            >
              Submit Enquiry
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}
